import { useState, useEffect } from 'react';
import { Database, Download, Upload, Trash2, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Backup {
  id: string;
  user_id: string;
  backup_type: string;
  created_at: string;
  created_by: string | null;
  notes: string;
}

interface User {
  id: string;
  email: string;
}

export function BackupsManagement() {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [backupNotes, setBackupNotes] = useState('');
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackupId, setRestoringBackupId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError('');

      const { data: usersData, error: usersError } = await supabase.rpc('get_all_users');
      if (usersError) throw usersError;

      const { data: backupsData, error: backupsError } = await supabase
        .from('data_backups')
        .select('*')
        .order('created_at', { ascending: false });

      if (backupsError) throw backupsError;

      setUsers(usersData || []);
      setBackups(backupsData || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateBackup() {
    if (!selectedUserId) {
      setError('Veuillez sélectionner un utilisateur');
      return;
    }

    setCreatingBackup(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const { error } = await supabase.rpc('create_user_backup', {
        target_user_id: selectedUserId,
        backup_type_param: 'manual',
        created_by_param: user.id,
        notes_param: backupNotes || ''
      });

      if (error) throw error;

      setSuccess('Sauvegarde créée avec succès');
      setBackupNotes('');
      setSelectedUserId('');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingBackup(false);
    }
  }

  async function handleRestoreBackup(backupId: string) {
    if (!confirm('Êtes-vous sûr de vouloir restaurer cette sauvegarde ? Toutes les données actuelles de l\'utilisateur seront remplacées.')) {
      return;
    }

    setRestoringBackupId(backupId);
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase.rpc('restore_user_backup', {
        backup_id_param: backupId
      });

      if (error) throw error;

      setSuccess('Sauvegarde restaurée avec succès');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRestoringBackupId(null);
    }
  }

  async function handleDeleteBackup(backupId: string) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette sauvegarde ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('data_backups')
        .delete()
        .eq('id', backupId);

      if (error) throw error;

      setSuccess('Sauvegarde supprimée avec succès');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  }

  function getUserEmail(userId: string) {
    const user = users.find(u => u.id === userId);
    return user?.email || 'Utilisateur inconnu';
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Database className="w-6 h-6" />
          Gestion des Sauvegardes
        </h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-emerald-800">{success}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Créer une Sauvegarde Manuelle</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Utilisateur
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Sélectionner un utilisateur</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={backupNotes}
              onChange={(e) => setBackupNotes(e.target.value)}
              placeholder="Raison de la sauvegarde..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <button
            onClick={handleCreateBackup}
            disabled={creatingBackup || !selectedUserId}
            className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {creatingBackup ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Création en cours...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Créer une Sauvegarde
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Sauvegardes Existantes</h3>
          <p className="text-sm text-gray-600 mt-1">
            {backups.length} sauvegarde(s) totale(s)
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {backups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Aucune sauvegarde disponible
                  </td>
                </tr>
              ) : (
                backups.map(backup => (
                  <tr key={backup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getUserEmail(backup.user_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        backup.backup_type === 'automatic'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {backup.backup_type === 'automatic' ? 'Automatique' : 'Manuelle'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(backup.created_at).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {backup.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleRestoreBackup(backup.id)}
                          disabled={restoringBackupId === backup.id}
                          className="text-emerald-600 hover:text-emerald-900 disabled:opacity-50"
                          title="Restaurer"
                        >
                          {restoringBackupId === backup.id ? (
                            <Loader className="w-5 h-5 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteBackup(backup.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
